// A script body, read-only, for people outside the writers' room (DESIGN rev 2.1 §3.3 Portal): the talent scripts page and
// the script share link. One row per script line, with the columns of the script's format (the same set the Scripts page
// writes, pages/scripts/scriptHelpers FORMAT_COLS; an unknown format reads as Skit, as the editor does). A Longform script
// reads as its episodes and scenes: each scene is a heading (number and description) over its lines. Text from the
// script goes through clean() for display. No API call: the body comes from the page.
import { Fragment, useMemo } from 'react';
import Table from '../ui/Table';
import EmptyState from '../ui/EmptyState';
import * as lfe from '../lib/longformEpisodes';
import { FORMAT_COLS } from '../pages/scripts/scriptHelpers';
import { clean } from '../shell/copy';

// "Scene Description" → "Scene description", "B-Roll" → "B-roll"; capitals that are acronyms (VFX) stay.
const sentenceCase = label => label.replace(/(\s|-)([A-Z][a-z]+)/g, (m, sep, word) => sep + word.toLowerCase());
const URL_ONLY = /^(https?:\/\/|www\.)\S+$/;

function columnsFor(format, drop = []) {
  const base = (FORMAT_COLS[format] || FORMAT_COLS.Skit).filter(c => !drop.includes(c.key));
  const total = base.reduce((n, c) => n + (parseFloat(c.width) || 0), 0);
  return base.map(c => ({
    key: c.key,
    label: sentenceCase(c.label),
    // the editor's proportions, scaled to the columns shown
    width: total && c.width ? `${(parseFloat(c.width) / total) * 100}%` : undefined,
    render: r => {
      const raw = String(r[c.key] ?? '').trim();
      if (!raw) return null;
      const label = <span className="ui-cell-label">{sentenceCase(c.label)}</span>;
      if (URL_ONLY.test(raw)) {
        const href = raw.startsWith('www.') ? `https://${raw}` : raw;
        return <>{label}<a className="ui-link ui-cell-text" href={href} target="_blank" rel="noreferrer noopener">Open link</a></>;
      }
      return <>{label}<span className={c.cls === 'line' ? 'ui-cell-text ui-cell-text--main' : 'ui-cell-text'}>{clean(raw)}</span></>;
    },
  }));
}

const keyed = (rows, offset = 0) => rows.map((r, i) => ({ ...r, _k: `${offset + i}:${r?.id || ''}` }));

// Longform: episodes (positional blocks between the EPISODE BREAK markers, lib/longformEpisodes) → scenes (contiguous runs
// of one scene number) → lines. The marker rows themselves are not lines.
function longformPlan(scenes) {
  return lfe.episodeBlocks(scenes).map(b => {
    const groups = [];
    for (const j of b.rowIndexes) {
      const r = scenes[j];
      if (lfe.isBreakMarker(r)) continue;
      const n = String(r?.sceneNumber ?? '').trim();
      const last = groups[groups.length - 1];
      if (last && last.number === n) last.rows.push({ ...r, _k: `${j}:${r?.id || ''}` });
      else groups.push({ key: `${b.index}:${j}`, number: n, description: String(r?.scene || '').trim(), rows: [{ ...r, _k: `${j}:${r?.id || ''}` }] });
    }
    return { index: b.index, title: lfe.blockTitle(scenes, b), groups };
  });
}

export default function ScriptReadView({ body }) {
  const format = body?.format;
  const scenes = useMemo(() => (Array.isArray(body?.scenes) ? body.scenes : []), [body]);
  const plan = useMemo(() => (format === 'Longform' ? longformPlan(scenes) : null), [format, scenes]);

  if (!scenes.length) return <EmptyState message="This script has no lines yet." />;

  if (plan) {
    const cols = columnsFor('Longform', ['sceneNumber', 'scene']);
    return (
      <div className="ui-stack ui-stack--loose ui-readrows">
        {plan.map(ep => (
          <section key={ep.index} className="ui-section" aria-label={plan.length > 1 ? `Episode ${ep.index}` : undefined}>
            {plan.length > 1 && <h2 className="ui-section__title">{ep.title ? `Episode ${ep.index}: ${clean(ep.title)}` : `Episode ${ep.index}`}</h2>}
            {ep.groups.map(g => (
              <Fragment key={g.key}>
                <h3 className="ui-heading ui-text-2">{[g.number && `Scene ${g.number}`, clean(g.description)].filter(Boolean).join(': ') || 'Lines'}</h3>
                <Table density="portal" fit={false} columns={cols} rows={g.rows} rowKey={r => r._k} caption={g.number ? `Scene ${g.number}` : 'Lines'} />
              </Fragment>
            ))}
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="ui-readrows">
      <Table density="portal" fit={false} columns={columnsFor(format)} rows={keyed(scenes)} rowKey={r => r._k} caption="Script" />
    </div>
  );
}
